import { atom } from "jotai";
import type { Stop } from "../types";

export const parkingRequestedLlAtom = atom<string | null>(null);

export function normalizeParkingName(name: string | null | undefined) {
  if (!name) return null;

  return String(name)
    .toLowerCase()
    .trim()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getPoiName(p: Stop): string {
  return (p as any).name ?? (p as any).label ?? "";
}

// Base-atoms
export const parkingEnabledAtom = atom<boolean>(false);
export const parkingRequestedNameAtom = atom<string | null>(null);
export const parkingPoisAtom = atom<Stop[]>([]);

export const requestedParkingPoiAtom = atom((get) => {
  const raw = get(parkingRequestedNameAtom);
  const wanted = normalizeParkingName(raw);
  const ll = parseLl(get(parkingRequestedLlAtom));
  const pois = get(parkingPoisAtom);

  if (pois.length === 0) return null;

  if (ll) {
    const byLl = findPoiByLl(pois, ll);
    if (byLl) return byLl;
  }

  if (!wanted) return null;

  const exact =
    pois.find((p) => normalizeParkingName(getPoiName(p)) === wanted) ?? null;

  if (exact) return exact;

  return (
    pois.find((p) => normalizeParkingName(getPoiName(p))?.includes(wanted)) ??
    null
  );
});

function parseLl(ll: string | null): { lat: number; lng: number } | null {
  if (!ll) return null;

  const [a, b] = ll.split(",").map((x) => Number(x.trim()));

  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  return { lat: a, lng: b };
}

function findPoiByLl(pois: Stop[], ll: { lat: number; lng: number }) {
  const tol = 0.001;

  let best: Stop | null = null;
  let bestD = Infinity;

  for (const p of pois) {
    const dLat = p.lat - ll.lat;
    const dLng = p.lng - ll.lng;

    if (Math.abs(dLat) > tol || Math.abs(dLng) > tol) continue;

    const d = dLat * dLat + dLng * dLng;

    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }

  return best;
}
